import { useEffect, useId, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Check, ChevronDown, Search } from 'lucide-react'
import { cn } from '@/lib/cn'
import { CONTROL } from './Field'

export interface ComboboxOption {
  value: string
  label: string
  /** Faint text after the label, also searched: a crawled name, "Disabled". */
  hint?: string
}

/** Every word of the query, in any order, anywhere in the label or hint. */
export function matchesQuery(option: ComboboxOption, query: string) {
  const text = `${option.label} ${option.hint ?? ''}`.toLowerCase()
  return query
    .toLowerCase()
    .split(/\s+/)
    .every((word) => text.includes(word))
}

/** The value of the "Create …" row: not an option's value. */
const CREATE = '__create__'

/**
 * A select with a search box, for lists too long to scroll: a client's
 * hundreds of programs, its slate list. The first row clears the value.
 * With `onCreate`, typed text no option is named exactly offers a last row
 * that creates it: the WB program picker's "type a new one" (PC-33).
 *
 * The popover is `position: fixed` under the trigger, so a table's or a
 * drawer's overflow does not clip it, and it closes when anything else
 * scrolls. It stays in the trigger's DOM, so a drawer's focus trap still
 * holds. Its Escape listener is on `window`, capture phase, which runs before
 * the overlay's on `document`: Escape closes the list, not the drawer.
 *
 * Clicks stop at each control, as in `TagEditor`: it is often inside a
 * clickable table row.
 */
export function Combobox({
  value,
  options,
  onChange,
  noneLabel,
  searchLabel,
  onCreate,
  className,
  ...trigger
}: {
  value: string | null
  options: ComboboxOption[]
  onChange: (next: string | null) => void
  /** The first row, which clears the value; also the trigger's text when nothing is chosen. */
  noneLabel: string
  /** The search box's placeholder and accessible name. */
  searchLabel: string
  /** Called with the typed text from the "Create …" row. */
  onCreate?: (text: string) => void
  className?: string
  id?: string
  'aria-label'?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [position, setPosition] = useState<CSSProperties>({})
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const listId = useId()

  const selected = options.find((o) => o.value === value)
  const rows = useMemo(() => {
    const typed = query.trim()
    const matches = typed ? options.filter((o) => matchesQuery(o, typed)) : options
    const exact = options.some((o) => o.label.trim().toLowerCase() === typed.toLowerCase())
    const create: ComboboxOption[] = onCreate && typed && !exact ? [{ value: CREATE, label: `Create “${typed}”` }] : []
    return [{ value: '', label: noneLabel } as ComboboxOption, ...matches, ...create]
  }, [options, query, noneLabel, onCreate])

  function openList() {
    const rect = triggerRef.current!.getBoundingClientRect()
    const below = window.innerHeight - rect.bottom
    // Below the trigger unless there is clearly more room above it.
    setPosition(
      below < 280 && rect.top > below
        ? { left: rect.left, bottom: window.innerHeight - rect.top + 4, width: Math.max(rect.width, 280) }
        : { left: rect.left, top: rect.bottom + 4, width: Math.max(rect.width, 280) },
    )
    setQuery('')
    setActive(Math.max(0, options.findIndex((o) => o.value === value) + 1))
    setOpen(true)
  }

  function close(refocus: boolean) {
    setOpen(false)
    if (refocus) triggerRef.current?.focus()
  }

  function choose(row: ComboboxOption) {
    if (row.value === CREATE) {
      onCreate?.(query.trim())
      close(true)
      return
    }
    const next = row.value || null
    if (next !== value) onChange(next)
    close(true)
  }

  useEffect(() => {
    if (!open) return
    function onPointerDown(e: MouseEvent) {
      const target = e.target as Node
      if (!popoverRef.current?.contains(target) && !triggerRef.current?.contains(target)) setOpen(false)
    }
    function onScroll(e: Event) {
      if (!popoverRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      setOpen(false)
      triggerRef.current?.focus()
    }
    const onResize = () => setOpen(false)
    document.addEventListener('mousedown', onPointerDown)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    window.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('keydown', onKeyDown, true)
    }
  }, [open])

  useEffect(() => {
    if (open) listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' })
  }, [open, active])

  const optionId = (i: number) => `${listId}-${i}`

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation()
          if (open) close(false)
          else openList()
        }}
        className={cn(CONTROL, 'flex h-9 items-center gap-2 text-left', className)}
        {...trigger}
      >
        <span className={cn('min-w-0 flex-1 truncate', !selected && 'text-ink-faint')}>
          {selected ? selected.label : noneLabel}
          {selected?.hint && <span className="text-ink-faint"> · {selected.hint}</span>}
        </span>
        <ChevronDown size={14} aria-hidden className="shrink-0 text-ink-faint" />
      </button>

      {open && (
        <div
          ref={popoverRef}
          style={position}
          className="fixed z-[60] flex max-h-[320px] flex-col overflow-hidden rounded-[6px] border border-rule bg-surface shadow-modal"
        >
          <div className="relative border-b border-rule p-1.5">
            <Search
              size={13}
              aria-hidden
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint"
            />
            <input
              autoFocus
              role="combobox"
              aria-expanded
              aria-controls={listId}
              aria-activedescendant={optionId(active)}
              aria-label={searchLabel}
              placeholder={searchLabel}
              value={query}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                setQuery(e.target.value)
                setActive(e.target.value.trim() ? 1 : 0)
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault()
                  const step = e.key === 'ArrowDown' ? 1 : -1
                  setActive((i) => Math.min(rows.length - 1, Math.max(0, i + step)))
                } else if (e.key === 'Enter') {
                  e.preventDefault()
                  const row = rows[active]
                  if (row) choose(row)
                } else if (e.key === 'Tab') {
                  close(false)
                }
              }}
              className="h-8 w-full rounded-[4px] border border-rule-strong bg-surface pl-7 pr-2 text-[13px] outline-none focus:border-brand-blue"
            />
          </div>
          <ul ref={listRef} id={listId} role="listbox" aria-label={searchLabel} className="min-h-0 overflow-y-auto py-1">
            {rows.map((row, i) => {
              const isSelected = row.value ? row.value === value : value === null
              return (
                <li
                  key={row.value || '__none'}
                  id={optionId(i)}
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={-1}
                  // Keep focus in the search box; the click picks.
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActive(i)}
                  onClick={(e) => {
                    e.stopPropagation()
                    choose(row)
                  }}
                  // Keys go to the search box (aria-activedescendant); this covers an option focused directly.
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') choose(row)
                  }}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[13px]',
                    i === active && 'bg-surface-sunk',
                    !row.value && 'text-ink-muted',
                  )}
                >
                  <Check size={12} aria-hidden className={cn('shrink-0', !isSelected && 'invisible')} />
                  <span className="min-w-0 flex-1 truncate">
                    {row.label}
                    {row.hint && <span className="text-ink-faint"> · {row.hint}</span>}
                  </span>
                </li>
              )
            })}
            {rows.length === 1 && !onCreate && (
              <li className="px-3 py-1.5 text-[12px] text-ink-faint">No matches</li>
            )}
          </ul>
        </div>
      )}
    </>
  )
}
